import React, { memo, useCallback } from "react";

import { HStack, Switch, Text } from "@chakra-ui/react";
import colors from "app/colors";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { FlagOutlineIcon, HelpIcon } from "modules/icons";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import {
  MODAL_TYPE,
  useOverlay,
} from "modules/providers/overlay-provider/overlay-provider";
import { TaskPriorityType } from "gql/graphql";

interface IProps {
  draftId: string;
}

const ComposeMakeTaskPriorityToggle = ({ draftId }: IProps) => {
  const { openModal } = useOverlay();
  const { draft, setTask } = useMessageThreadDraft({
    draftId,
  });

  const onToggleClicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!draft || !draft.task) {
        return;
      }
      setTask({
        ...draft.task,
        priorityType: e.target.checked
          ? TaskPriorityType.High
          : TaskPriorityType.None,
      });
    },
    [draft, setTask],
  );

  const onHelpClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      openModal({
        type: MODAL_TYPE.HELP,
        helpModal: {
          icon: <FlagOutlineIcon boxSize={100} fill={colors.darkBlue} />,
          title: "Make Priority",
          message:
            "Toggle this setting on for tasks that have a greater urgency or importance.  Tasks with the flag icon will appear higher in the task list when sorted by other criteria.",
        },
      });
    },
    [openModal],
  );

  const priorityType = draft?.task?.priorityType ?? TaskPriorityType.None;

  if (!draft) return null;

  return (
    <HStack spacing={0}>
      <Switch
        size="sm"
        isChecked={priorityType === TaskPriorityType.High}
        onChange={onToggleClicked}
      />
      <Text fontSize={14} color={colors.gray50} pl={2} m={0}>
        Make Priority
      </Text>
      <CircularIconButton
        icon={<HelpIcon boxSize={4} fill={colors.gray40} />}
        minW={6}
        h={6}
        aria-label="Make priority info"
        onClick={onHelpClick}
      />
    </HStack>
  );
};

export default memo(ComposeMakeTaskPriorityToggle);
